/**
 * Shared schedule-repeat helpers for harvest requests, campaigns, and SMS.
 *
 * API write fields (backend computes scheduled_publish_date):
 *   schedule_type: "one_time" | "daily" | "weekly" | "monthly" | "yearly"
 *   schedule_time: "HH:MM:SS"
 *   schedule_date: "YYYY-MM-DD"  — once / weekly
 *   schedule_months: [1–12]      — monthly (integers)
 *   schedule_day_of_month: 1–31   — monthly
 */

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const DAYS_OF_WEEK = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
]

export const HARVEST_REPEAT_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'one_time', label: 'Once' },
  { value: 'weekly', label: 'Day Of The Week' },
  { value: 'monthly', label: 'Monthly' },
]

export const CAMPAIGN_REPEAT_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'one_time', label: 'Once' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

const JS_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1)

export const monthNameToNumber = (name) => {
  const idx = MONTH_NAMES.indexOf(name)
  return idx >= 0 ? idx + 1 : null
}

export const monthNumberToName = (num) => {
  const n = Number(num)
  return n >= 1 && n <= 12 ? MONTH_NAMES[n - 1] : ''
}

/** UI month names or API integers → [1–12] */
export const monthsToApiNumbers = (months = []) =>
  months
    .map((m) => (typeof m === 'number' ? m : monthNameToNumber(m)))
    .filter((n) => n >= 1 && n <= 12)
    .sort((a, b) => a - b)

/** API integers → UI month name strings */
export const monthsFromApi = (months = []) => {
  if (!Array.isArray(months) || !months.length) return []
  if (typeof months[0] === 'number') {
    return months.map(monthNumberToName).filter(Boolean)
  }
  return months.filter((m) => MONTH_NAMES.includes(m))
}

export const formatScheduleTime = (time) => {
  if (!time) return null
  const { hours, minutes } = parseTimeParts(time)
  const h = String(hours).padStart(2, '0')
  const m = String(minutes).padStart(2, '0')
  return `${h}:${m}:00`
}

export const parseScheduleTimeForInput = (time) => {
  if (!time) return '00:00'
  const parts = String(time).split(':')
  return `${parts[0]?.padStart(2, '0') || '00'}:${parts[1]?.padStart(2, '0') || '00'}`
}

const getDaysInMonth = (year, monthIndex) =>
  new Date(year, monthIndex + 1, 0).getDate()

const clampDay = (year, monthIndex, dayOfMonth) => {
  const max = getDaysInMonth(year, monthIndex)
  return Math.min(Math.max(1, Number(dayOfMonth) || 1), max)
}

const parseTimeParts = (time) => {
  if (!time) return { hours: 0, minutes: 0 }

  const trimmed = String(time).trim()
  const ampmMatch = trimmed.match(/\s*(AM|PM)$/i)
  const base = ampmMatch ? trimmed.replace(/\s*(AM|PM)$/i, '').trim() : trimmed
  const parts = base.split(':').map((part) => parseInt(part, 10))
  let hours = Number.isFinite(parts[0]) ? parts[0] : 0
  const minutes = Number.isFinite(parts[1]) ? parts[1] : 0

  if (ampmMatch) {
    const meridiem = ampmMatch[1].toUpperCase()
    if (meridiem === 'PM' && hours < 12) hours += 12
    if (meridiem === 'AM' && hours === 12) hours = 0
  }

  return { hours, minutes }
}

const localDateString = (date = new Date()) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const applyTime = (date, time) => {
  if (!time) return date
  const { hours, minutes } = parseTimeParts(time)
  date.setHours(hours, minutes, 0, 0)
  return date
}

const resolveOneTimeScheduleDate = ({ date, months, dayOfMonth, time }) => {
  if (months?.length && dayOfMonth) {
    return buildDateFromMonthDay(months, dayOfMonth, time)
  }
  return date || null
}

export const buildDateFromMonthDay = (months, dayOfMonth, time) => {
  const monthNums = monthsToApiNumbers(months)
  if (!monthNums.length || !dayOfMonth) return null
  const monthIdx = monthNums[0] - 1
  const now = new Date()
  let year = now.getFullYear()
  let day = clampDay(year, monthIdx, dayOfMonth)
  let d = applyTime(new Date(year, monthIdx, day), time)
  if (d <= now) {
    year += 1
    day = clampDay(year, monthIdx, dayOfMonth)
    d = applyTime(new Date(year, monthIdx, day), time)
  }
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

export const buildWeeklyScheduleDate = (day, time) => {
  const targetDayIdx = JS_DAYS.indexOf(day)
  if (targetDayIdx < 0) return null
  const now = new Date()
  const diff = (targetDayIdx - now.getDay() + 7) % 7
  const nextDate = new Date(now)
  nextDate.setDate(now.getDate() + (diff === 0 && applyTime(new Date(nextDate), time || '00:00') <= now ? 7 : diff))
  const y = nextDate.getFullYear()
  const m = String(nextDate.getMonth() + 1).padStart(2, '0')
  const dd = String(nextDate.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

export function computeScheduledPublishDate({
  repeat,
  date,
  time,
  day,
  months,
  month,
  dayOfMonth,
}) {
  if (!repeat) return null

  if (repeat === 'one_time') {
    const scheduleDate = resolveOneTimeScheduleDate({ date, months, dayOfMonth, time })
    if (scheduleDate && time) {
      const d = applyTime(new Date(`${scheduleDate}T00:00:00`), time)
      return Number.isNaN(d.getTime()) ? null : d
    }
    return null
  }

  if (!time) return null
  const now = new Date()

  if (repeat === 'daily') {
    const d = applyTime(new Date(`${localDateString(now)}T00:00:00`), time)
    if (d <= now) d.setDate(d.getDate() + 1)
    return Number.isNaN(d.getTime()) ? null : d
  }

  if (repeat === 'weekly') {
    const scheduleDate = date || buildWeeklyScheduleDate(day, time)
    if (!scheduleDate) return null
    const d = applyTime(new Date(`${scheduleDate}T00:00:00`), time)
    if (d <= now) {
      d.setDate(d.getDate() + 7)
    }
    return Number.isNaN(d.getTime()) ? null : d
  }

  if (repeat === 'monthly') {
    const selectedMonths = monthsToApiNumbers(months?.length ? months : month ? [month] : [])
      .map((n) => n - 1)

    if (!selectedMonths.length || !dayOfMonth) return null

    const candidates = []
    for (const year of [now.getFullYear(), now.getFullYear() + 1]) {
      for (const monthIdx of selectedMonths) {
        const day = clampDay(year, monthIdx, dayOfMonth)
        const d = applyTime(new Date(year, monthIdx, day), time)
        if (d > now) candidates.push(d)
      }
    }

    candidates.sort((a, b) => a - b)
    return candidates[0] || null
  }

  return null
}

export function validateScheduleConfig(config) {
  const { repeat, date, time, day, months, dayOfMonth } = config
  const errors = {}

  if (repeat === 'one_time') {
    const usesMonthPicker = Boolean(months?.length && dayOfMonth)
    if (!usesMonthPicker && !date) errors.months = 'Select a month and day.'
    if (usesMonthPicker && !months?.length) errors.months = 'Select a month.'
    if (usesMonthPicker && !dayOfMonth) errors.dayOfMonth = 'Select a day of the month.'
    if (!time) errors.time = 'Time is required.'
  } else if (repeat === 'weekly') {
    if (!day) errors.day = 'Day is required.'
    if (!time) errors.time = 'Time is required.'
  } else if (repeat === 'monthly') {
    if (!months?.length) errors.months = 'Select at least one month.'
    if (!dayOfMonth) errors.dayOfMonth = 'Day is required.'
    if (!time) errors.time = 'Time is required.'
  } else if (repeat === 'daily') {
    if (!time) errors.time = 'Time is required.'
  }

  const scheduledAt = computeScheduledPublishDate(config)
  if (Object.keys(errors).length === 0 && scheduledAt && scheduledAt.getTime() <= Date.now()) {
    errors.scheduleFuture = 'Scheduled date and time must be in the future.'
  }

  return errors
}

export function buildScheduleApiFields(config) {
  const repeat = config.repeat || 'one_time'
  const fields = { schedule_type: repeat }
  const scheduleTime = formatScheduleTime(config.time)

  if (repeat === 'one_time') {
    const scheduleDate = resolveOneTimeScheduleDate({
      date: config.date,
      months: config.months,
      dayOfMonth: config.dayOfMonth,
      time: config.time,
    })
    if (scheduleDate) fields.schedule_date = scheduleDate
    if (scheduleTime) fields.schedule_time = scheduleTime
  }

  if (repeat === 'daily' && scheduleTime) {
    fields.schedule_time = scheduleTime
  }

  if (repeat === 'weekly') {
    const scheduleDate = config.date || buildWeeklyScheduleDate(config.day, config.time)
    if (scheduleDate) fields.schedule_date = scheduleDate
    if (scheduleTime) fields.schedule_time = scheduleTime
  }

  if (repeat === 'monthly') {
    fields.schedule_months = monthsToApiNumbers(config.months)
    fields.schedule_day_of_month = Number(config.dayOfMonth)
    if (scheduleTime) fields.schedule_time = scheduleTime
  }

  return fields
}

/** Map GET response schedule fields into UI state */
export function parseScheduleFromApi(data = {}) {
  const repeat = data.schedule_type || 'one_time'
  const time = parseScheduleTimeForInput(data.schedule_time)
  const result = {
    repeat,
    time,
    day: 'Monday',
    date: '',
    months: [],
    dayOfMonth: '',
    hasSchedule: false,
  }

  if (data.scheduled_publish_date) {
    const dt = new Date(data.scheduled_publish_date)
    if (!Number.isNaN(dt.getTime()) && dt.getFullYear() !== 1970) {
      result.hasSchedule = true
      result.date = data.schedule_date || dt.toISOString().slice(0, 10)
      result.day = dt.toLocaleDateString('en-US', { weekday: 'long' })
      if (!data.schedule_time) {
        result.time = parseScheduleTimeForInput(dt.toTimeString().slice(0, 8))
      }
    }
  }

  if (data.schedule_date) {
    result.date = data.schedule_date
    result.hasSchedule = true
    const dt = new Date(`${data.schedule_date}T12:00:00`)
    if (!Number.isNaN(dt.getTime())) {
      result.day = dt.toLocaleDateString('en-US', { weekday: 'long' })
    }
  }

  if (repeat === 'monthly') {
    result.months = monthsFromApi(data.schedule_months || [])
    result.dayOfMonth = data.schedule_day_of_month || ''
    if (result.months.length || result.dayOfMonth) result.hasSchedule = true
  }

  if (repeat === 'one_time' && result.date) {
    const dt = new Date(`${result.date}T12:00:00`)
    if (!Number.isNaN(dt.getTime())) {
      result.months = [MONTH_NAMES[dt.getMonth()]]
      result.dayOfMonth = dt.getDate()
    }
  }

  return result
}

/** Append schedule fields to FormData (campaigns) */
export function appendScheduleFieldsToFormData(formData, scheduleFields) {
  formData.append('schedule_type', scheduleFields.schedule_type || 'one_time')
  if (scheduleFields.schedule_date) {
    formData.append('schedule_date', scheduleFields.schedule_date)
  }
  if (scheduleFields.schedule_time) {
    formData.append('schedule_time', scheduleFields.schedule_time)
  }
  if (scheduleFields.schedule_day_of_month != null) {
    formData.append('schedule_day_of_month', String(scheduleFields.schedule_day_of_month))
  }
  if (scheduleFields.schedule_months?.length) {
    scheduleFields.schedule_months.forEach((m) => formData.append('schedule_months', String(m)))
  }
}
